import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ChevronRight,
  Download,
  Menu,
  MessageSquareText,
  PackageX,
  Search,
  ShieldAlert,
  Sparkles,
  Star,
  Target,
  X,
} from "lucide-react";
import type { QualityDashboardResponse } from "@ailan/contracts";
import { getQualityDashboard } from "../api/client";

type QualitySession = QualityDashboardResponse["sessions"][number];
type InsightCategory = "使用反馈" | "商品需求" | "商品损坏" | "过敏/不适";

type CustomerEvidence = {
  id: string;
  sessionId: string;
  sourceConversationId: string;
  productName: string;
  platform: string;
  storeName: string;
  agentName: string;
  customerName: string;
  time: string;
  content: string;
};

type InsightRule = {
  demand: string;
  improvement: string;
  category: InsightCategory;
};

type ProductInsight = InsightRule & {
  key: string;
  productName: string;
  evidence: CustomerEvidence[];
  platforms: string[];
  explicitCount: number;
  priority: number;
};

type InsightFilters = {
  startDate: string;
  endDate: string;
  platform: string;
  store: string;
  agent: string;
};

const categoryOrder: InsightCategory[] = ["使用反馈", "商品需求", "商品损坏", "过敏/不适"];
const platformLabels: Record<string, string> = { tmall: "天猫", douyin: "抖音", pdd: "拼多多", kuaishou: "快手", jd: "京东", xiaohongshu: "小红书" };
const requestPattern = /希望|建议|需要|想要|最好|能不能|可不可以|增加|改进|改善|优化|补充|加强|推出|出一款|能出/;
const newProductPattern = /新品|新产品|新商品|增加一款|新增一款|添加一款|推出一款|开发一款|出一款|能不能出|希望.{0,8}出|建议.{0,8}(增加|新增|推出|开发)/;
const damagedPattern = /坏了|破损|漏液|碎了|碎裂|破裂|压坏|凹陷|变质|异味|泵头.{0,5}坏|瓶口.{0,5}坏|无法使用/;
const allergyPattern = /过敏|发红|红肿|刺痛|瘙痒|发痒|起疹|长疹|烂脸|脱皮|灼热|不适/;
const productPattern = /效果|肤感|清洁|紧绷|味道|香味|质地|颜色|尺寸|规格|容量|质量|成分|配方|包装|瓶口|泵头|商品|产品|洁面|面膜|精油|身体乳|防晒|精华|面霜|洗发|沐浴/;
const productNames = ["洁面乳", "面膜", "护发精油", "身体乳", "防晒霜", "防晒", "精华液", "精华", "面霜", "洗发水", "沐浴露", "爽肤水", "乳液"];

function inputDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function defaultFilters(): InsightFilters {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  return { startDate: inputDate(start), endDate: inputDate(end), platform: "", store: "", agent: "" };
}

function datesBetween(startDate: string, endDate: string) {
  const start = new Date(`${startDate}T00:00:00+08:00`);
  const end = new Date(`${endDate}T00:00:00+08:00`);
  const dates: string[] = [];
  for (const date = start; date <= end && dates.length < 31; date.setDate(date.getDate() + 1)) dates.push(inputDate(date));
  return dates;
}

function platformOf(session: QualitySession) {
  return platformLabels[session.platform] ?? session.platform;
}

function extractProductName(content: string, summary: string) {
  const text = `${content} ${summary}`;
  return productNames.find((name) => text.includes(name)) ?? "待关联商品";
}

function requestRule(text: string): InsightRule {
  if (newProductPattern.test(text)) return { demand: "客户建议新增或开发商品", improvement: "建立新品需求池，提取客户想要的功能、配方、规格和使用场景，按提及频次评估立项。", category: "商品需求" };
  if (/规格|容量|便携|大瓶|小瓶|旅行装|补充装/.test(text)) return { demand: "客户希望增加或调整商品规格", improvement: "统计客户期望的容量和使用场景，评估便携装、补充装或多规格组合。", category: "商品需求" };
  if (/香味|味道|无香|香型/.test(text)) return { demand: "客户希望调整商品香味", improvement: "归纳香味偏好与不接受原因，评估无香版或不同香型。", category: "商品需求" };
  if (/成分|配方|敏感肌|温和|刺激/.test(text)) return { demand: "客户希望优化配方或适用人群", improvement: "汇总肤质和使用反应，评估配方温和度并完善适用人群说明。", category: "商品需求" };
  if (/包装|瓶口|泵头|按压|开盖/.test(text)) return { demand: "客户希望改善商品包装设计", improvement: "根据客户原话复盘开盖、按压、密封和携带体验，形成包装改版清单。", category: "商品需求" };
  return { demand: "客户提出商品功能或体验改进需求", improvement: "汇总客户原话和使用场景，按出现频次评估产品功能与体验改进。", category: "商品需求" };
}

function evaluationRule(text: string): InsightRule {
  if (/效果|没效果|改善|见效/.test(text)) return { demand: "客户反馈商品效果与预期存在差异", improvement: "按使用周期和人群复核效果反馈，优化产品定位和效果说明。", category: "使用反馈" };
  if (/肤感|紧绷|油腻|黏|粘|清洁/.test(text)) return { demand: "客户反馈商品使用感受需要改善", improvement: "按肤质、用量和使用方法归纳肤感问题，评估配方与使用说明改进。", category: "使用反馈" };
  if (/味道|香味|质地|颜色/.test(text)) return { demand: "客户反馈商品感官体验需要改善", improvement: "统计香味、质地和颜色反馈的集中方向，评估产品体验调整。", category: "使用反馈" };
  return { demand: "客户反馈商品本身的使用体验", improvement: "按商品、平台和使用场景汇总客户原话，提炼可执行的商品改善项。", category: "使用反馈" };
}

function matchingRules(text: string) {
  const matches: InsightRule[] = [];
  if (allergyPattern.test(text)) matches.push({ demand: "客户反馈使用后过敏或不适", improvement: "优先核实症状、肤质、用法和商品批次，评估配方风险并完善敏感提示。", category: "过敏/不适" });
  if (damagedPattern.test(text)) matches.push({ demand: "客户反馈商品损坏或无法正常使用", improvement: "记录损坏位置和批次，复盘包材、密封、出库质检和运输防护。", category: "商品损坏" });
  if (newProductPattern.test(text) || (requestPattern.test(text) && productPattern.test(text))) matches.push(requestRule(text));
  if (productPattern.test(text)) matches.push(evaluationRule(text));
  return matches.filter((rule, index) => matches.findIndex((item) => item.category === rule.category) === index);
}

function priorityOf(evidence: CustomerEvidence[], category: InsightCategory) {
  const riskBonus = category === "过敏/不适" ? 45 : category === "商品损坏" ? 28 : category === "商品需求" ? 12 : 0;
  return Math.min(100, 30 + evidence.length * 12 + riskBonus);
}

function priorityLabel(value: number) {
  return value >= 75 ? "优先改善" : value >= 55 ? "持续关注" : "常规观察";
}

function createDemoSessions(date: string): QualitySession[] {
  const at = (time: string) => new Date(`${date}T${time}:00+08:00`).toISOString();
  const makeSession = (id: string, platform: QualitySession["platform"], storeName: string, agentName: string, customerName: string, time: string, messages: string[]): QualitySession => ({
    id: `insight-demo-${id}`, sourceConversationId: `模拟-INSIGHT-${id}`, platform, storeName, agentName, customerName,
    orderId: null, yellowFlagged: null, startedAt: at(time), endedAt: at(time), collectedAt: at(time), score: 100,
    status: "COMPLETED", summary: "商品洞察模拟会话", source: "DEMO_PRODUCT_INSIGHT",
    messages: messages.map((content, index) => ({ id: `${id}-${index}`, sender: "CUSTOMER", sentAt: at(time), content, flagged: false })), issues: [],
  });
  return [
    makeSession("001", "tmall", "爱兰歌娜旗舰店", "郑佳婷", "模拟顾客A", "09:12", ["这款洁面乳洗完有点紧绷，希望可以再温和一点，敏感肌也能用。"]),
    makeSession("002", "douyin", "Algn爱兰歌娜生活旗舰店", "殷筱婷", "模拟顾客B", "10:26", ["这个面膜用完脸有点发红刺痛，我担心是不是过敏了。"]),
    makeSession("003", "pdd", "爱兰歌娜拼多多店", "冉佳欣", "模拟顾客C", "13:18", ["护发精油收到以后泵头坏了，按不出来，希望包装能改进。"]),
    makeSession("004", "kuaishou", "爱兰歌娜快手店", "郑佳婷", "模拟顾客D", "15:42", ["身体乳能不能出一个无香版和小容量旅行装？携带会方便很多。"]),
    makeSession("005", "tmall", "爱兰歌娜旗舰店", "冉佳欣", "模拟顾客E", "17:06", ["建议增加一款敏感肌可以用的修护面霜，最好是无香配方。"]),
  ];
}

export function ProductInsightsPage({ onMenuOpen }: { onMenuOpen: () => void }) {
  const [sessions, setSessions] = useState<QualitySession[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showingDemo, setShowingDemo] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [draftFilters, setDraftFilters] = useState<InsightFilters>(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState<InsightFilters>(defaultFilters);
  const [selected, setSelected] = useState<ProductInsight | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    const dates = datesBetween(appliedFilters.startDate, appliedFilters.endDate);
    void Promise.all(dates.map((date) => getQualityDashboard(date).catch(() => null))).then((dashboards) => {
      if (!active) return;
      const realSessions = dashboards.flatMap((dashboard) => dashboard?.sessions ?? []);
      const deduplicated = [...new Map(realSessions.map((session) => [session.id, session])).values()];
      if (deduplicated.length) {
        setSessions(deduplicated);
        setShowingDemo(false);
      } else {
        setSessions(createDemoSessions(appliedFilters.endDate));
        setShowingDemo(true);
        setLoadError(dashboards.every((dashboard) => dashboard === null));
      }
    }).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [appliedFilters.endDate, appliedFilters.startDate]);

  const sourceSessions = useMemo(() => sessions.filter((session) => (!appliedFilters.platform || platformOf(session) === appliedFilters.platform)
    && (!appliedFilters.store || session.storeName === appliedFilters.store)
    && (!appliedFilters.agent || session.agentName === appliedFilters.agent)), [appliedFilters.agent, appliedFilters.platform, appliedFilters.store, sessions]);

  const insights = useMemo(() => {
    const groups = new Map<string, { rule: InsightRule; evidence: CustomerEvidence[] }>();
    sourceSessions.forEach((session) => session.messages.filter((message) => message.sender === "CUSTOMER").forEach((message) => {
      const text = message.content.trim();
      if (!text) return;
      const extractedProductName = extractProductName(text, session.summary);
      const productName = extractedProductName === "待关联商品" && newProductPattern.test(text) ? "待规划新品" : extractedProductName;
      matchingRules(text).forEach((rule) => {
        const key = `${productName}::${rule.category}::${rule.demand}`;
        const evidence: CustomerEvidence = {
          id: `${session.id}-${message.id}`, sessionId: session.id, sourceConversationId: session.sourceConversationId,
          productName, platform: platformOf(session), storeName: session.storeName,
          agentName: session.agentName, customerName: session.customerName ?? "客户匿名", time: message.sentAt, content: text,
        };
        const existing = groups.get(key);
        groups.set(key, { rule, evidence: [...(existing?.evidence ?? []), evidence] });
      });
    }));
    return [...groups.entries()].map(([key, grouped]) => ({
      ...grouped.rule, key, productName: grouped.evidence[0]!.productName, evidence: grouped.evidence,
      platforms: [...new Set(grouped.evidence.map((item) => item.platform))],
      explicitCount: grouped.evidence.filter((item) => requestPattern.test(item.content)).length,
      priority: priorityOf(grouped.evidence, grouped.rule.category),
    } satisfies ProductInsight)).sort((left, right) => right.priority - left.priority || right.evidence.length - left.evidence.length);
  }, [sourceSessions]);

  const filteredInsights = useMemo(() => {
    const keyword = search.trim().toLocaleLowerCase();
    return insights.filter((insight) => (!keyword || [insight.productName, insight.demand, insight.improvement]
      .some((value) => value.toLocaleLowerCase().includes(keyword))) && (!category || insight.category === category));
  }, [category, insights, search]);

  const products = new Set(insights.map((insight) => insight.productName)).size;
  const feedbackCount = (name: InsightCategory) => insights.filter((insight) => insight.category === name).reduce((sum, insight) => sum + insight.evidence.length, 0);
  const platforms = [...new Set(sessions.map(platformOf))];
  const stores = [...new Set(sessions.map((session) => session.storeName))];
  const agents = [...new Set(sessions.map((session) => session.agentName))];
  const maxEvidence = Math.max(...insights.map((insight) => insight.evidence.length), 1);
  const categoryStats = categoryOrder.map((name) => {
    const categoryInsights = insights.filter((insight) => insight.category === name);
    return { name, count: categoryInsights.reduce((sum, insight) => sum + insight.evidence.length, 0), themes: categoryInsights.length };
  });
  const maxCategoryCount = Math.max(...categoryStats.map((item) => item.count), 1);

  return <main className="main productInsightsMain">
    <header className="topbar"><button className="menuButton" aria-label="打开导航" onClick={onMenuOpen}><Menu /></button><strong>商品洞察</strong><span>客户声音与商品改善机会</span></header>
    <div className="productInsightsContent">
      <section className="productInsightsHead"><div><h1>商品洞察</h1><p>从客户日常聊天中识别使用反馈、改进需求、新品建议、损坏和过敏反馈，帮助持续改善商品。</p></div></section>

      <section className="productInsightsNotice"><Sparkles /><div><strong>{showingDemo ? "当前展示模拟客户会话" : "当前分析真实客户会话"}</strong><span>只分析客户发送的商品相关内容，物流、退款和客服态度等非商品问题不会进入洞察；所有结论都保留客户原话。</span></div></section>
      {loadError && <section className="operationsNotice amber"><AlertTriangle /><div><strong>真实会话暂时读取失败</strong><span>当前使用明确标注的模拟参考，不计入真实统计。</span></div></section>}

      <section className="productInsightsQuery">
        <label className="date"><span>日期（最多31天）</span><div><input type="date" value={draftFilters.startDate} onChange={(event) => setDraftFilters((current) => ({ ...current, startDate: event.target.value }))} /><i>至</i><input type="date" value={draftFilters.endDate} onChange={(event) => setDraftFilters((current) => ({ ...current, endDate: event.target.value }))} /></div></label>
        <label><span>平台</span><select value={draftFilters.platform} onChange={(event) => setDraftFilters((current) => ({ ...current, platform: event.target.value }))}><option value="">全部平台</option>{platforms.map((item) => <option key={item}>{item}</option>)}</select></label>
        <label><span>店铺</span><select value={draftFilters.store} onChange={(event) => setDraftFilters((current) => ({ ...current, store: event.target.value }))}><option value="">全部店铺</option>{stores.map((item) => <option key={item}>{item}</option>)}</select></label>
        <label><span>客服</span><select value={draftFilters.agent} onChange={(event) => setDraftFilters((current) => ({ ...current, agent: event.target.value }))}><option value="">全部客服</option>{agents.map((item) => <option key={item}>{item}</option>)}</select></label>
        <button className="button primary" disabled={loading} onClick={() => setAppliedFilters({ ...draftFilters })}><Search />{loading ? "分析中" : "查询"}</button>
      </section>

      <section className="productInsightsSummary">
        <header><div><i /><h2>客户声音概览</h2><span>{sourceSessions.length} 个会话 · {products} 款商品</span></div><button className="button" onClick={() => window.print()}><Download />导出报告</button></header>
        <div className="productInsightsKpis">
          <article><span>使用反馈</span><strong>{feedbackCount("使用反馈")}</strong><small>客户对效果和使用体验的反馈</small><Star /></article>
          <article><span>商品需求</span><strong>{feedbackCount("商品需求")}</strong><small>客户明确提出的商品改进要求</small><MessageSquareText /></article>
          <article><span>商品损坏</span><strong>{feedbackCount("商品损坏")}</strong><small>破损、漏液或无法正常使用</small><PackageX /></article>
          <article className="urgent"><span>过敏 / 不适</span><strong>{feedbackCount("过敏/不适")}</strong><small>发红、刺痛、瘙痒或起疹等</small><ShieldAlert /></article>
        </div>
      </section>

      <section className="productInsightsOverview">
        <div className="productDemandPanel"><header><div><h2>高频商品改善需求</h2><p>只统计客户原话中与商品本身有关的内容</p></div></header><div>{insights.slice(0, 6).map((insight) => <button key={insight.key} onClick={() => setSelected(insight)}><span><strong>{insight.demand}</strong><small>{insight.productName}</small></span><i><b style={{ width: `${insight.evidence.length / maxEvidence * 100}%` }} /></i><em>{insight.evidence.length}条</em><ChevronRight /></button>)}</div></div>
        <aside className="productOpportunityPanel"><header><div><h2>商品改善优先级</h2><p>过敏和损坏反馈优先处理</p></div><Target /></header><div>{insights.slice(0, 5).map((insight) => <button key={insight.key} onClick={() => setSelected(insight)}><b>{insight.priority}</b><span><strong>{insight.productName}</strong><small>{insight.category} · {priorityLabel(insight.priority)}</small></span></button>)}</div></aside>
      </section>

      <section className="productInsightCategories"><header><div><i /><h2>按客户反馈类别分布</h2></div><span>CATEGORY</span></header><div>{categoryStats.map((item) => <button key={item.name} className={category === item.name ? "active" : ""} onClick={() => setCategory((current) => current === item.name ? "" : item.name)}><span><strong>{item.name}</strong><small>{item.themes} 个改善主题</small></span><i><b style={{ width: `${item.count / maxCategoryCount * 100}%` }} /></i><em>{item.count} 条原话</em></button>)}</div></section>

      <section className="productInsightsTablePanel">
        <header><div><h2>客户声音与商品改善明细</h2><p>共 {filteredInsights.length} 条洞察</p></div><div className="productInsightsFilters"><label><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索商品、客户需求或改善方向" /></label><select value={category} onChange={(event) => setCategory(event.target.value)}><option value="">全部类型</option>{categoryOrder.map((item) => <option key={item}>{item}</option>)}</select></div></header>
        <div className="productInsightsTableScroll"><table><thead><tr><th>商品</th><th>客户反馈主题</th><th>商品改善方向</th><th>涉及平台</th><th>客户原话</th><th>来源</th><th>优先级</th></tr></thead><tbody>{filteredInsights.map((insight) => <tr key={insight.key} onClick={() => setSelected(insight)}><td><strong>{insight.productName}</strong><small>{insight.category}</small></td><td>{insight.demand}</td><td>{insight.improvement}</td><td><div className="productInsightPlatformTags">{insight.platforms.map((platform) => <span key={platform}>{platform}</span>)}</div></td><td><strong>{insight.evidence.length} 条</strong><small>{insight.evidence[0]?.content}</small></td><td><span className="explicit">客户日常会话</span></td><td><b className={insight.priority >= 75 ? "high" : "normal"}>{insight.priority} · {priorityLabel(insight.priority)}</b></td></tr>)}</tbody></table>{!loading && !filteredInsights.length && <div className="operationsEmpty">当前会话中没有识别到商品改善相关的客户声音</div>}</div>
      </section>
    </div>

    {selected && <div className="modalLayer" onMouseDown={(event) => event.target === event.currentTarget && setSelected(null)}><section className="productInsightModal"><header><div><strong>{selected.demand}</strong><span>{selected.productName} · {selected.category}</span></div><button className="iconButton" aria-label="关闭" onClick={() => setSelected(null)}><X /></button></header><div className="productInsightModalBody"><section><span>商品改善建议</span><p>{selected.improvement}</p><div><strong>分析口径</strong><span>只根据客户在日常会话中的原话归纳；不把客服态度、物流或退款问题计入商品洞察。</span></div></section><aside><span>客户原话与会话证据</span>{selected.evidence.map((item) => <article key={item.id}><header><strong>{item.platform} · {item.storeName}</strong><small>{new Date(item.time).toLocaleString("zh-CN")}</small></header><p>{item.content}</p><footer><span>{item.sourceConversationId} · {item.agentName}</span><b>{item.customerName}</b></footer></article>)}</aside></div><footer><button className="button" onClick={() => setSelected(null)}>关闭</button></footer></section></div>}
  </main>;
}
